import { __awaiter, __generator, __assign } from 'tslib';
import { useMemo, useEffect } from 'react';
import { useAuthRefresh, formsQuerySelector, availableLocaleListSelector } from '@keepeek/commons';
import { useRecoilState, useSetRecoilState, useRecoilValueLoadable } from 'recoil';
import { useCurrentUser } from '../users/hooks.js';
import { indexationAtom, indexationProgressionAtom, IndexationStep } from './atoms.js';
import { ClassificationDestinationType } from './types.js';
import { createClassification, createCustomIndexationRetryAxiosInstance, sendBulkIndexation, addMediaInSelection, createUserSelection, importElementInWall } from './utils.js';

function useIndexation(_a) {
    var _this = this;
    var formTypeFormIdMappings = _a.formTypeFormIdMappings, kpkMediaImports = _a.kpkMediaImports, overrideClassificationStep = _a.overrideClassificationStep, indexInAllAvailableLocale = _a.indexInAllAvailableLocale, _b = _a.classificationDestination, classificationDestination = _b === void 0 ? {
        type: ClassificationDestinationType.CLASSIFICATION_VALIDATION_REQUEST
    } : _b;
    var refresh = useAuthRefresh().refresh;
    var _c = useRecoilState(indexationAtom), indexationState = _c[0], setIndexationState = _c[1];
    var setIndexationProgression = useSetRecoilState(indexationProgressionAtom);
    var currentUser = useCurrentUser().currentUser;
    var formsQuery = useRecoilValueLoadable(formsQuerySelector);
    var allowedForms = formsQuery.state === "hasValue" ? formsQuery.contents : null;
    var userId = currentUser === null || currentUser === void 0 ? void 0 : currentUser.id;
    var availableLocaleListLoadable = useRecoilValueLoadable(availableLocaleListSelector);
    var availableLocaleList = useMemo(function () {
        return availableLocaleListLoadable.state === "hasValue" ? availableLocaleListLoadable.contents : [];
    }, [availableLocaleListLoadable.contents, availableLocaleListLoadable.state]);
    useEffect(function () {
        if (!userId) {
            console.debug("do not execute indexation because userid is not defined");
            return;
        }
        if (indexationState.error) {
            console.debug("do not execute indexation because error state");
            return;
        }
        var execute = function () { return __awaiter(_this, void 0, void 0, function () {
            var _a, error_1;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, 14, , 15]);
                        _a = indexationState.step;
                        switch (_a) {
                            case IndexationStep.INITIAL: return [3 /*break*/, 1];
                            case IndexationStep.IMPORT_ELEMENT_IN_WALL: return [3 /*break*/, 2];
                            case IndexationStep.CREATE_USER_SELECTION: return [3 /*break*/, 5];
                            case IndexationStep.ADD_MEDIA_IN_SELECTION: return [3 /*break*/, 7];
                            case IndexationStep.SEND_BULK_INDEXATION: return [3 /*break*/, 9];
                            case IndexationStep.CREATE_CLASSIFICATION: return [3 /*break*/, 11];
                        }
                        return [3 /*break*/, 13];
                    case 1: 
                    // do nothing
                    return [3 /*break*/, 13];
                    case 2: 
                    // First, take a fresh token before import to be sure to be valid during at least the next 10min
                    return [4 /*yield*/, refresh(true)];
                    case 3:
                        // First, take a fresh token before import to be sure to be valid during at least the next 10min
                        _b.sent();
                        return [4 /*yield*/, importElementInWall(createCustomIndexationRetryAxiosInstance(), indexationState, setIndexationState, setIndexationProgression, kpkMediaImports, allowedForms, formTypeFormIdMappings)];
                    case 4:
                        _b.sent();
                        return [3 /*break*/, 13];
                    case 5: return [4 /*yield*/, createUserSelection(createCustomIndexationRetryAxiosInstance(), setIndexationState, userId)];
                    case 6:
                        _b.sent();
                        return [3 /*break*/, 13];
                    case 7: return [4 /*yield*/, addMediaInSelection(createCustomIndexationRetryAxiosInstance(), indexationState, setIndexationState, userId)];
                    case 8:
                        _b.sent();
                        return [3 /*break*/, 13];
                    case 9: return [4 /*yield*/, sendBulkIndexation(createCustomIndexationRetryAxiosInstance(), indexationState, setIndexationState, availableLocaleList, userId, indexInAllAvailableLocale)];
                    case 10:
                        _b.sent();
                        return [3 /*break*/, 13];
                    case 11: return [4 /*yield*/, createClassification(createCustomIndexationRetryAxiosInstance(), indexationState, setIndexationState, setIndexationProgression, classificationDestination, overrideClassificationStep)];
                    case 12:
                        _b.sent();
                        return [3 /*break*/, 13];
                    case 13: return [3 /*break*/, 15];
                    case 14:
                        error_1 = _b.sent();
                        console.error(error_1);
                        setIndexationState(function (old) {
                            return __assign(__assign({}, old), { error: true });
                        });
                        return [3 /*break*/, 15];
                    case 15: return [2 /*return*/];
                }
            });
        }); };
        execute();
    }, [
        allowedForms,
        availableLocaleList,
        classificationDestination,
        formTypeFormIdMappings,
        indexInAllAvailableLocale,
        indexationState,
        kpkMediaImports,
        overrideClassificationStep,
        refresh,
        setIndexationProgression,
        setIndexationState,
        userId,
    ]);
    function process(formFields) {
        setIndexationState(function (old) {
            return __assign(__assign({}, old), { formFields: formFields, error: false, hasRightError: false, needNewUploadErrors: [], step: old.step === IndexationStep.INITIAL ? IndexationStep.IMPORT_ELEMENT_IN_WALL : old.step });
        });
    }
    function reset() {
        setIndexationState({
            step: IndexationStep.INITIAL,
            formFields: [],
            mediaIds: [],
            descriptorsHref: [],
            selectionId: "",
            error: false,
            hasRightError: false,
            needNewUploadErrors: [],
            mediaIdsAlreadyClassified: []
        });
        setIndexationProgression(undefined);
    }
    return {
        process: process,
        processStep: indexationState.step,
        reset: reset,
        error: indexationState.error,
        rightError: indexationState.hasRightError,
        needNewUploadErrors: indexationState.needNewUploadErrors
    };
}

export { useIndexation };
//# sourceMappingURL=hooks.js.map
